<template>
  <div class="variants__wrapper">
    <div class="variants">
      <Button
        v-for="variant in variants"
        :key="variant.id"
        width="full"
        theme="secondary"
        form="outline"
        class="variant"
        :class="{
          'variant--disabled': variantDisabled(variant),
          'variant--selected': selectedVariant?.id === variant.id,
        }"
        @click="handleVariantSelect(variant)"
      >
        {{ variant.title }}
      </Button>
    </div>
    <div class="variants-meta">
      <button
        v-if="hasVariantsOutOfStock"
        class="variants-button"
        @click="handleStockNotificationClick"
      >
        <span class="text">{{ buttonText.outOfStock }}</span>
      </button>
      <button class="variants-button" @click="setOpenSizeGuide(true)">
        <span class="text">{{ buttonText.sizeGuide }}</span>
      </button>
    </div>
  </div>
</template>

<script>
// Import components
import Button from "@/components/ButtonV2.vue";

// Import internal modules
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "Variants",
  components: {
    Button,
  },
  data() {
    return {
      buttonText: {
        sizeGuide: "Size Guide",
        outOfStock: "Size Not In Stock?",
      },
    };
  },
  computed: {
    ...mapState("product", ["product", "selectedVariant"]),
    ...mapGetters("product", ["variants"]),
    hasVariantsOutOfStock() {
      return this.variants.some((variant) => {
        return !(variant.quantityAvailable > 0 && variant.availableForSale);
      });
    },
    isSoldOut() {
      if (this.availableForPreOrder(this.selectedVariant)) {
        return false;
      }

      return (
        !this.selectedVariant.availableForSale ||
        this.selectedVariant.quantityAvailable <= 0
      );
    },
  },
  methods: {
    ...mapActions("product", [
      "setSelectedVariant",
      "updateProductUrlV2",
      "setOpenSizeGuide",
    ]),
    ...mapActions("add-to-bag", [
      "showCompactStockNotif",
      "openStockNotification",
    ]),
    handleVariantSelect(variant) {
      this.setSelectedVariant(variant);
      this.updateProductUrlV2();
      this.showCompactStockNotif({
        product: this.product,
        variant,
        show: this.isSoldOut,
      });
    },
    handleStockNotificationClick() {
      this.setSelectedVariant(null);
      this.openStockNotification({
        product: this.product,
      });
    },
    variantDisabled(variant) {
      const variantIsPreOrder = this.availableForPreOrder(variant);

      if (variantIsPreOrder) {
        return false;
      }

      return !variant.availableForSale || variant.quantityAvailable <= 0;
    },
    preOrder(variant) {
      const preorder = variant?.randbPreorder?.value;
      if (!preorder) return false;

      return JSON.parse(preorder);
    },
    availableForPreOrder(variant) {
      if (!this.preOrder) return false;

      const {
        enabled = false,
        limit = 0,
        reserved = 0,
      } = this.preOrder(variant);
      const inventory = variant.quantityAvailable || 0;

      return enabled && inventory <= 0 && reserved < limit;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.variants {
  display: grid;
  grid-template-columns: repeat(4, minmax(81px, 1fr));
  gap: 8px;
  justify-items: flex-start;
  align-items: center;

  & > * {
    display: block;
    width: 100%;
    word-wrap: break-word;
  }
}

.variant {
  padding: 11px 0;
  cursor: pointer;
  border-radius: 4px;
  border-color: $color-grey;

  &:hover {
    opacity: 1;
  }

  &--disabled {
    background-color: $color-grey-light;
    border-color: $color-grey;
    color: $color-grey;

    &:hover {
      background-color: $color-grey-light;
      border-color: $color-grey;
      color: $color-grey;
    }
  }

  &--selected {
    background-color: $color-black;
    color: $color-white;
    border-color: $color-black;

    &:hover {
      background-color: $color-black;
      color: $color-white;
    }
  }

  &--selected.variant--disabled {
    border-color: $color-black;
    background-color: $color-grey-light;
    color: $color-black;

    &:hover {
      border-color: $color-black;
      background-color: $color-grey-light;
      color: $color-black;
    }
  }

  &--added-to-bag {
    background-color: $color-black;
    color: $color-white;

    &:hover {
      background-color: $color-black;
      color: $color-white;
    }
  }
}

.variants-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 11px;
}

.variants-button {
  padding: 15px 28px;
  background-color: $color-grey-light;
  color: $color-grey-dark;
  border-radius: 4px;
  @include utility-button(13px);
  border: none;
  outline: none;
  text-decoration: none;
  text-align: center;
  text-transform: none;

  padding: 0;
  color: $color-grey-dark;
  background-color: transparent;
  outline: none;
  border: none;
  @include utility-label(15px);

  .text {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: 1px;
      background: $color-grey;
      bottom: -6px;
    }
  }
}
</style>
